import { useCallback, useMemo, useRef, useState } from 'react'

import { faCalendar, faCalendarWeek, faCalendarDay, faPen } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'antd'
import { Dayjs } from 'dayjs'
import styled from 'styled-components'
import { TimeZone } from 'types/graphql'

import Text from 'src/components/Typography/Text'
import { useCurrentBreakpoint } from 'src/hooks/use-current-breakpoint'
import useDisclosure from 'src/hooks/use-disclosure'
import { KeyboardKey } from 'src/types'
import {
  beginningOfDayInTimeZone,
  DateFormats,
  endOfDayInTimeZone,
  getDayjsInTimeZone,
} from 'src/utils/date'

import HStack from '../Flex/HStack'
import FormControl from '../FormControl/FormControl'
import { FormControlProps } from '../FormControl/util'
import Icon from '../Icon/Icon'
import { MenuContentStyles, MenuItemStyleProps, MenuItemStyles } from '../Menu'
import { useMenuKeyboardShortcuts } from '../Menu/use-menu-keyboard-shortcuts'
import Popover from '../Popover/Popover'

import { CustomDateRangePopover } from './CustomDateRangePopover'
import { getDateRangeDisplay, getPresetDateRange } from './util'

const StyledContent = styled(Popover.Content)`
  ${MenuContentStyles};
  padding: 6px;
`

export const StyledItem = styled.div<MenuItemStyleProps>`
  ${MenuItemStyles};
`

export enum DateRangeType {
  Today = 'Today',
  Yesterday = 'Yesterday',
  PastWeek = 'Past week',
  MonthToDate = 'Month to date',
  Past4Weeks = 'Past 4 weeks',
  Past12Weeks = 'Past 12 weeks',
  YearToDate = 'Year to date',
  Past6Months = 'Past 6 months',
  Past12Months = 'Past 12 months',
  Custom = 'Custom',
}

const presets = [
  DateRangeType.Today,
  DateRangeType.Yesterday,
  DateRangeType.PastWeek,
  DateRangeType.MonthToDate,
  DateRangeType.Past4Weeks,
  DateRangeType.Past12Weeks,
  DateRangeType.YearToDate,
  DateRangeType.Past6Months,
  DateRangeType.Past12Months,
]

export type DateRangePickerValue = {
  type: DateRangeType
  startDate?: Dayjs
  endDate?: Dayjs
}

type Props = Omit<FormControlProps, 'isLabelRedundant'> & {
  dateFormat: DateFormats
  timeZone?: TimeZone
  value: DateRangePickerValue

  onChange: (value: DateRangePickerValue) => void
}

const DateRangePicker = ({
  caption,
  dateFormat,
  errorMessage,
  id,
  isInvalid,
  isRequired,
  label,
  onChange,
  timeZone,
  tooltip,
  value,
  width,
}: Props) => {
  const { isOpen: isDropdownOpen, open: openDropdown, close: closeDropdown } = useDisclosure()
  const {
    isOpen: isCustomPopoverOpen,
    open: openCustomPopover,
    close: closeCustomPopover,
  } = useDisclosure()
  const [activeIndex, setActiveIndex] = useState(0)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const customIndex = presets.length

  const handleOnChange = useCallback(
    (onChangeValue: DateRangePickerValue) => {
      const startDate =
        onChangeValue.startDate && timeZone
          ? beginningOfDayInTimeZone(onChangeValue.startDate, timeZone)
          : onChangeValue.startDate?.startOf('day')
      const endDate =
        onChangeValue.endDate && timeZone
          ? endOfDayInTimeZone(onChangeValue.endDate, timeZone)
          : onChangeValue.endDate?.endOf('day')
      onChange({ type: onChangeValue.type, startDate, endDate })
    },
    [onChange, timeZone]
  )

  const handleCloseDropdown = useCallback(() => {
    setActiveIndex(0) // reset active index
    closeDropdown()
    requestAnimationFrame(() => {
      buttonRef.current?.focus()
    })
  }, [closeDropdown])

  const handleSelect = useCallback(() => {
    if (activeIndex === customIndex) {
      openCustomPopover()
      return
    }
    const type = presets[activeIndex]
    if (!type) {
      return
    }

    const { startDate, endDate } = getPresetDateRange(type, timeZone)
    handleOnChange({ type, startDate, endDate })
    handleCloseDropdown()
  }, [activeIndex, customIndex, timeZone, handleOnChange, handleCloseDropdown, openCustomPopover])

  const handlePopoverClose = useCallback(() => {
    if (isCustomPopoverOpen) {
      closeCustomPopover()
      return
    }
    handleCloseDropdown()
  }, [closeCustomPopover, handleCloseDropdown, isCustomPopoverOpen])

  const { menuItemRefs } = useMenuKeyboardShortcuts({
    activeIndex,
    count: presets.length + 1,
    isEnabled: isDropdownOpen && !isCustomPopoverOpen,
    onEscape: handlePopoverClose,
    onSelect: handleSelect,
    setActiveIndex,
  })

  const { selectedStart, selectedEnd } = useMemo(() => {
    if (value.type === DateRangeType.Custom) {
      return {
        selectedStart:
          timeZone && value.startDate
            ? getDayjsInTimeZone(value.startDate, timeZone)
            : value.startDate,
        selectedEnd:
          timeZone && value.endDate ? getDayjsInTimeZone(value.endDate, timeZone) : value.endDate,
      }
    }
    const { startDate, endDate } = getPresetDateRange(value.type, timeZone)
    return { selectedStart: startDate, selectedEnd: endDate }
  }, [value, timeZone])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key === KeyboardKey.Enter ||
        e.key === KeyboardKey.ArrowDown ||
        e.key === KeyboardKey.Space
      ) {
        openDropdown()
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [openDropdown]
  )

  const handleOpenCustomPopover = useCallback(() => {
    setActiveIndex(customIndex)
    openCustomPopover()
  }, [customIndex, openCustomPopover])

  const handleSelectPreset = useCallback(
    (index: number) => {
      const type = presets[index]
      if (!type) {
        return
      }
      const { startDate, endDate } = getPresetDateRange(type, timeZone)
      handleOnChange({ type, startDate, endDate })
      handleCloseDropdown()
    },
    [handleCloseDropdown, handleOnChange, timeZone]
  )

  const getDateRangeMenuItemLabel = useCallback((type: DateRangeType) => {
    let icon = faCalendar
    switch (type) {
      case DateRangeType.Today:
      case DateRangeType.Yesterday:
        icon = faCalendarDay
        break
      case DateRangeType.PastWeek:
        icon = faCalendarWeek
        break
      case DateRangeType.Custom:
        icon = faPen
        break
    }

    return (
      <HStack gap={2}>
        <Text colorToken="colorIcon">
          <FontAwesomeIcon icon={icon} />
        </Text>
        {type}
      </HStack>
    )
  }, [])

  const { isSmallScreen } = useCurrentBreakpoint()

  return (
    <Popover isOpen={isDropdownOpen} onOpen={openDropdown} onClose={handleCloseDropdown}>
      <Popover.Trigger asChild>
        <div style={{ width }}>
          <FormControl
            caption={caption}
            errorMessage={errorMessage}
            id={id}
            isInvalid={isInvalid}
            isRequired={isRequired}
            label={label}
            tooltip={tooltip}
          >
            {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
              <Button
                onKeyDown={handleInputKeyDown}
                aria-describedby={ariaDescribedBy}
                aria-invalid={ariaInvalid}
                aria-live={ariaLive}
                style={{ width: '100%' }}
                ref={buttonRef}
              >
                <HStack gap={3} justify="space-between">
                  <div>{getDateRangeDisplay(selectedStart, selectedEnd, timeZone)}</div>
                  <Text colorToken="colorIcon">
                    <Icon.FaCalendar />
                  </Text>
                </HStack>
              </Button>
            )}
          </FormControl>
        </div>
      </Popover.Trigger>
      <StyledContent align="start" sideOffset={6} tabIndex={-1} role="menu">
        {presets.map((p, index) => (
          <StyledItem
            key={p}
            role="menuitem"
            onClick={() => handleSelectPreset(index)}
            onMouseMove={() => setActiveIndex(index)}
            ref={(el) => menuItemRefs.current.set(index, el)}
            data-selected={index === activeIndex}
          >
            {getDateRangeMenuItemLabel(p)}
          </StyledItem>
        ))}
        <Popover
          isOpen={isCustomPopoverOpen}
          onOpen={openCustomPopover}
          onClose={closeCustomPopover}
        >
          <Popover.Trigger asChild>
            <StyledItem
              role="menuitem"
              onClick={handleOpenCustomPopover}
              onMouseMove={() => setActiveIndex(customIndex)}
              ref={(el) => menuItemRefs.current.set(customIndex, el)}
              data-selected={customIndex === activeIndex}
            >
              {getDateRangeMenuItemLabel(DateRangeType.Custom)}
            </StyledItem>
          </Popover.Trigger>
          <StyledContent
            align="start"
            side={isSmallScreen() ? 'bottom' : 'right'}
            sideOffset={6}
            onEscapeKeyDown={handlePopoverClose}
            role="dialog"
          >
            <CustomDateRangePopover
              startDate={selectedStart}
              endDate={selectedEnd}
              onChange={handleOnChange}
              onClose={handlePopoverClose}
              dateFormat={dateFormat}
            />
          </StyledContent>
        </Popover>
      </StyledContent>
    </Popover>
  )
}

export default React.memo(DateRangePicker)
